import React, { useCallback, useEffect, useRef, useState } from "react";

const TOOLTIP_SHOW_DELAY = 1500;
const TOOLTIP_HIDE_DELAY = 150;

interface AtButtonProps {
  active: boolean;
  disabled?: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
}

export const AtButton: React.FC<AtButtonProps> = ({ active, disabled = false, onMouseDown }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (showTimer.current) {
      clearTimeout(showTimer.current);
      showTimer.current = null;
    }
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  useEffect(() => {
    return clearTimers;
  }, [clearTimers]);

  useEffect(() => {
    if (active) {
      clearTimers();
      setShowTooltip(false);
    }
  }, [active, clearTimers]);

  const handleMouseEnter = useCallback(() => {
    if (active || disabled) {
      return;
    }
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    showTimer.current = setTimeout(() => {
      setShowTooltip(true);
    }, TOOLTIP_SHOW_DELAY);
  }, [active, disabled]);

  const handleMouseLeave = useCallback(() => {
    if (showTimer.current) {
      clearTimeout(showTimer.current);
      showTimer.current = null;
    }
    hideTimer.current = setTimeout(() => {
      setShowTooltip(false);
    }, TOOLTIP_HIDE_DELAY);
  }, []);

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        type="button"
        className={
          "w-8 h-8 rounded-full flex items-center justify-center transition-colors " +
          (disabled
            ? "cursor-not-allowed bg-gray-100 text-gray-500 opacity-50"
            : active
            ? "bg-blue-100 text-blue-600"
            : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700")
        }
        aria-label="Edit existing block or type @"
        disabled={disabled}
        onMouseDown={onMouseDown}
      >
        <span className="material-icons !text-lg">alternate_email</span>
      </button>
      {showTooltip && !active && (
        <div
          role="tooltip"
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs whitespace-nowrap shadow-lg animate-in fade-in duration-150 z-50"
        >
          <span className="font-medium">Edit existing block</span>
          <span className="text-gray-400 ml-1">or type @</span>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
};
